import { Fragment, useState } from 'react';
import type { ProvisioningPreview } from '@arcnet/contracts';
import { useDevices, useProvisioningLogin, useDeleteDevice } from '../../lib/api/hooks-extension.js';
import { useProvisioningVendors, usePreviewConfig } from '../../lib/api/hooks-provisioning.js';
import {
  TableSkeleton, Empty, ErrorBanner, Badge, StatusBadge, SectionHead,
} from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { useSession } from '../../lib/auth/session.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { relativeTime } from '../../lib/format.js';
import { VENDOR_LABEL, LoginPanel, Preview } from './ProvisioningBits.js';

/**
 * Provisioning: how handsets get their configuration.
 *
 * The portal half of the provisioning module (API: modules/provisioning). It
 * answers what the Handsets list cannot: has this phone ever fetched a
 * configuration, may it fetch one now, and what exactly will it be given.
 *
 * Handsets themselves -- MAC, model, which extensions sit on which line key --
 * are set up on each extension, under Phone setup. That is the phone's identity;
 * this is its supply of configuration, across the account. A handset left on no
 * extension has nowhere else to be seen, so it can be removed here.
 */
export function ProvisioningTab({ tenantId, onNavigate }: {
  tenantId: string;
  onNavigate?: (section: string) => void;
}) {
  const can = useSession((s) => s.can);
  const devices = useDevices(tenantId);
  const vendors = useProvisioningVendors();
  const loginQ = useProvisioningLogin(tenantId);
  const [login, setLogin] = useState<{ deviceId: string; url: string | null; username: string; password: string } | null>(null);
  const del = useDeleteDevice(tenantId);
  const preview = usePreviewConfig(tenantId);
  const step = useStepUp();
  const [shown, setShown] = useState<{ deviceId: string; data: ProvisioningPreview } | null>(null);

  const rows = devices.data?.data ?? [];
  const mayOpen = can('device:provision');
  const mayWrite = can('device:write');
  const mayReveal = can('extension:credentials:reveal');
  const generated = (vendors.data?.data ?? []).filter((v) => v.generates_config).map((v) => v.label);

  // Never fetched: the phone needs the URL and its provisioning login typed in. Said once at the
  // top, because the handset itself shows nothing but a failure to register.
  const stuck = rows.filter((d) => d.status === 'unprovisioned' && !d.provisioned_at);
  const showLogin = async (deviceId: string) => {
    await step.run(async () => {
      const r = await loginQ.mutateAsync(deviceId);
      setLogin({ deviceId, ...r });
      return r;
    });
  };

  const load = async (deviceId: string, reveal: boolean) => {
    await step.run(async () => {
      const data = await preview.mutateAsync({ deviceId, reveal });
      setShown({ deviceId, data });
      return data;
    });
  };

  return (
    <>
      <SectionHead title="Provisioning"
        sub={generated.length
          ? `How handsets fetch their configuration. ${generated.join(' and ')} receive a generated file; other makes get the settings listed for entering by hand.`
          : 'How handsets fetch their configuration, and whether they can right now.'} />

      {devices.error && <ErrorBanner error={devices.error} />}
      <StepUpPrompt step={step} what="Showing a handset's provisioning login or its SIP password" />

      {stuck.length > 0 && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            {stuck.length} handset{stuck.length === 1 ? ' has' : 's have'} never fetched a
            configuration. Enter the provisioning URL and the handset's login on the phone (Login, below).
          </span>
        </div>
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Handset</th><th>Phone</th><th>Configuration</th>
                <th>Last fetched</th><th />
              </tr>
            </thead>
            {devices.data === undefined ? <TableSkeleton cols={5} /> : (
              <tbody>
                {rows.map((d) => (
                  <Fragment key={d.id}>
                    <tr>
                      <td>
                        <div className="arc-stack">
                          <span style={{ fontWeight: 550 }}>{d.label ?? d.mac_address ?? 'Handset'}</span>
                          {d.label && d.mac_address && (
                            <span className="arc-subtle arc-mono" style={{ fontSize: 11.5 }}>{d.mac_address}</span>
                          )}
                          {d.lines.length === 0 && (
                            <span className="arc-subtle" style={{ fontSize: 11.5 }}>on no extension</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="arc-stack">
                          <span>{VENDOR_LABEL[d.vendor] ?? d.vendor}</span>
                          {d.model && <span className="arc-subtle" style={{ fontSize: 11.5 }}>{d.model}</span>}
                        </div>
                      </td>
                      <td><StatusBadge status={d.status} /></td>
                      <td className="arc-muted">
                        {d.provisioned_at ? relativeTime(d.provisioned_at) : <span className="arc-subtle">never</span>}
                      </td>
                      <td className="arc-td-right">
                        <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                            disabled={preview.isPending}
                            onClick={() => {
                              if (shown?.deviceId === d.id) setShown(null);
                              else void load(d.id, false);
                            }}>
                            {shown?.deviceId === d.id ? 'Close' : 'Preview'}
                          </button>
                          {mayOpen && mayReveal && (
                            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={loginQ.isPending}
                              title="The URL, user name and password to type into the phone"
                              onClick={() => { if (login?.deviceId === d.id) setLogin(null); else void showLogin(d.id); }}>
                              {login?.deviceId === d.id ? 'Hide login' : 'Login'}
                            </button>
                          )}
                          {mayWrite && d.lines.length === 0 && (
                            <RowAction kind="delete" label={`Remove ${d.label ?? d.mac_address ?? 'handset'}`}
                              disabled={del.isPending}
                              onClick={() => {
                                if (confirm(`Remove ${d.label ?? d.mac_address ?? 'this handset'}? It is on no extension.`)) {
                                  del.mutate(d.id);
                                }
                              }} />
                          )}
                        </div>
                      </td>
                    </tr>
                    {login?.deviceId === d.id && (
                      <tr>
                        <td colSpan={5} style={{ padding: 12, background: 'var(--arc-surface-2)' }}>
                          <LoginPanel login={login} onClose={() => setLogin(null)} />
                        </td>
                      </tr>
                    )}
                    {shown?.deviceId === d.id && (
                      <tr>
                        <td colSpan={5} style={{ padding: 12, background: 'var(--arc-surface-2)' }}>
                          <Preview data={shown.data} canReveal={mayReveal}
                            onReveal={() => void load(d.id, true)} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {devices.data !== undefined && rows.length === 0 && (
          <Empty icon="phone" title="No handsets"
            hint="Add a phone by its MAC address from an extension's Phone setup."
            {...(onNavigate ? {
              action: (
                <button type="button" className="arc-btn arc-btn-sm" onClick={() => onNavigate('extensions')}>
                  Go to Extensions
                </button>
              ),
            } : {})} />
        )}
      </div>
    </>
  );
}
